import React, { useEffect } from 'react';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { MarketingPage, Navbar, Footer } from '@lunarcrush/ds/marketing';
import { Divider } from '@lunarcrush/ds';
import { useAppNavigation } from './hooks/useAppNavigation';
import { usePageMeta } from './hooks/usePageMeta';
import { useCMS } from './cms';
import MobileNav from './MobileNav';
import { PlatformMegaMenu } from './PlatformMegaMenu';
import { LanguagePicker } from './LanguagePicker';
import { ThemePicker } from './ThemePicker';
import { NavSearchButton } from './NavSearchButton';
import { SearchOverlay } from './SearchOverlay';
import { DeferredSection } from './components/DeferredSection';

const FOOTER_COLUMN_KEYS: Record<string, string> = {
  Platform: 'nav.platform',
  Product: 'footer.columns.product',
  Company: 'footer.columns.company',
  Resources: 'footer.columns.resources',
  Legal: 'footer.columns.legal',
};

const FOOTER_LINK_KEYS: Record<string, string> = {
  'LunarCrush + Claude': 'nav.products.claude',
  'LunarCrush + ChatGPT': 'nav.products.chatgpt',
  'LunarCrush API': 'nav.products.api',
  'LunarCrush MCP': 'nav.products.mcp',
  'LunarCrush Discover': 'nav.products.discover',
  'LunarCrush CLI': 'nav.products.cli',
  'LunarCrush Collections': 'nav.products.collections',
  'Custom Collections': 'nav.products.customCollections',
  Shop: 'footer.links.shop',
  FAQs: 'footer.links.faqs',
  About: 'footer.links.about',
  Careers: 'footer.links.careers',
  'API Docs': 'footer.links.apiDocs',
};

/** Label used for the docs entry injected into the Platform footer column. */
const API_DOCS_LABEL = 'API Docs';

/** Insert the API reference directly after the API product page in the
 *  Platform column. No-op for every other column, and for a Platform column
 *  that somehow has no API entry, so a CMS reshuffle cannot produce a
 *  stranded link. */
function withApiDocs(columnTitle: string, links: string[]): string[] {
  if (columnTitle !== 'Platform') return links;
  const at = links.indexOf('LunarCrush API');
  if (at === -1 || links.includes(API_DOCS_LABEL)) return links;
  return [...links.slice(0, at + 1), API_DOCS_LABEL, ...links.slice(at + 1)];
}

/** Footer label -> destination. Single source for the anchor's `href` and the
 *  router push, so a link can never navigate somewhere other than where it says
 *  it goes (DEV-10940). Absolute URLs are external; `/` paths owned by the
 *  marketing router are pushed client-side, and `/developers/*` is left to the
 *  browser because that is the webapp, not this app. */
const FOOTER_LINK_HREFS: Record<string, string> = {
  [API_DOCS_LABEL]: '/developers/api',
  'LunarCrush + Claude': '/products/lunarcrush-claude',
  'LunarCrush + ChatGPT': '/products/lunarcrush-chatgpt',
  'LunarCrush API': '/products/lunarcrush-api',
  'LunarCrush MCP': '/products/lunarcrush-mcpserver',
  'LunarCrush Discover': '/products/lunarcrush-discover',
  'LunarCrush CLI': '/products/lunarcrush-cli',
  'LunarCrush Collections': '/products/lunarcrush-custom-collections',
  'Custom Collections': '/products/lunarcrush-custom-collections',
  Shop: 'https://shop.lunarcrush.com/',
  FAQs: '/support',
  Glossary: '/metrics',
  About: '/about',
  Careers: '/careers',
  'Brand Resources': '/brand',
};

const Layout: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const navProps = useAppNavigation();
  const { t } = useTranslation();
  const { pages, footerColumns, footerSocialLinks } = useCMS();
  usePageMeta();

  useEffect(() => {
    let lastY = window.scrollY;
    const onScroll = () => {
      const y = window.scrollY;
      document.documentElement.classList.toggle('scrolled', y > 10);
      // Hide nav on scroll down, show on scroll up (only after scrolling past nav height)
      if (y > 80) {
        document.documentElement.classList.toggle('nav-hidden', y > lastY);
      } else {
        document.documentElement.classList.remove('nav-hidden');
      }
      lastY = y;
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    onScroll();
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  // DEV-11294: the global ⌘K / Ctrl+K shortcut lives inside SearchOverlay
  // (mounted below), which opens in place instead of redirecting to the app.
  const selfNavbarPages = ['/', '/enterprise', '/use-cases', '/pricing', '/support', '/terms', '/privacy', '/referral-terms', '/products/lunarcrush-claude', '/products/lunarcrush-chatgpt', '/products/lunarcrush-api', '/products/lunarcrush-mcpserver', '/products/lunarcrush-discover', '/products/lunarcrush-cli', '/products/lunarcrush-custom-collections', '/products/fantasy-football', '/about', '/crypto-social-listening', '/careers', '/blog', '/brand', '/metrics'];
  const path = location.pathname.length > 1 && location.pathname.endsWith('/') ? location.pathname.slice(0, -1) : location.pathname;
  const hasSelfNavbar = selfNavbarPages.includes(path)
    || path.startsWith('/support/')
    || path.startsWith('/blog/')
    || path.startsWith('/use-cases/')
    || path.startsWith('/metrics/');

  return (
    <MarketingPage>
      {/* DEV-11294: in-place search — one instance serves every page,
          including the self-navbar pages rendered through the Outlet. */}
      <SearchOverlay />
      {/* Pages with hero sections render their own Navbar for visual layering */}
      {!hasSelfNavbar && (
        <>
          <div className="sticky-nav desktop-nav">
            <Navbar
              logo={
                <a href="/" style={{ display: 'flex' }}>
                  <img
                    src="/landing-assets/lunarCrush-header-logo.png"
                    alt="LunarCrush"
                    style={{ width: 140, height: 32, objectFit: 'contain' }}
                  />
                </a>
              }
              {...navProps}
              extras={<><NavSearchButton /><ThemePicker /><LanguagePicker /></>}
            />
            <PlatformMegaMenu />
          </div>
          <div className="sticky-nav mobile-nav">
            <MobileNav />
          </div>
        </>
      )}

      <main>
        <Outlet />
      </main>

      {/* DEV-12444: footer is below the fold on every marketing page. Mounting
          it in the initial Layout commit stretched the homepage's first React
          task. Reserve height so the upgrade does not shift layout. */}
      <DeferredSection minHeight={420}>
      <Divider />

      {/* ─── Footer ────────────────────────────────────────── */}
      <div className="footer-wrap ms">
        <Footer
          logo={
            <img
              src="/landing-assets/lunarCrush-header-logo.png"
              alt="LunarCrush"
              style={{ width: 120, height: 28, objectFit: 'contain' }}
            />
          }
          tagline={pages.shared.footer_tagline}
          columns={footerColumns.map((col) => ({
            title: FOOTER_COLUMN_KEYS[col.title] ? t(FOOTER_COLUMN_KEYS[col.title]) : col.title,
            // DEV-10940: the reference sits right under the product page it
            // documents, so the pair reads as "what the API is" then "how to
            // call it". Injected here rather than added to the CMS because the
            // docs are a webapp route, not a marketing page, so the CMS has no
            // record to hang it off.
            links: withApiDocs(col.title, col.links).map((label) => {
              // DEV-10940: one map of label -> destination, used for BOTH the
              // href and the click handler. Previously only the handler
              // existed, so every footer item rendered as a div: fine for a
              // mouse, invisible to a crawler, and impossible to open in a new
              // tab. Deriving both from the same entry keeps them in step.
              const href = FOOTER_LINK_HREFS[label];
              // `/developers/*` is the webapp, a separate build on the same
              // host. The marketing router does not own those paths, so a
              // client-side push would land on a route that does not exist.
              // Leaving `onPress` undefined lets the anchor do a real
              // navigation.
              const routable = !!href && href.startsWith('/') && !href.startsWith('/developers');
              return {
                label: FOOTER_LINK_KEYS[label] ? t(FOOTER_LINK_KEYS[label]) : label,
                href,
                onPress: routable ? () => navigate(href) : undefined,
              };
            }),
          }))}
          copyright={pages.shared.footer_copyright}
          legalLinks={[
            { label: t('footer.terms'), href: '/terms' },
            { label: t('footer.privacy'), href: '/privacy' },
          ]}
          socialLinks={footerSocialLinks.map((label) => ({
            label,
            href: ({
              X: 'https://x.com/LunarCrush',
              Telegram: 'https://t.me/lunarcrush',
              YouTube: 'https://www.youtube.com/@LunarCrush',
              Instagram: 'https://www.instagram.com/lunarcrush_app/',
            } as Record<string, string>)[label],
          }))}
        />
      </div>
      </DeferredSection>
    </MarketingPage>
  );
};

export default Layout;
